import { EMediaType } from "@frontend-shared/types";
import { FileArchive, Music, Video, X } from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

import type { IDownloadIndicatorItemProps } from "./types";

// NOTE: NSAI-3847 — a "Download all" reports as the archive it produces, so
// the row says what is actually being saved rather than naming the recording
// that happens to be the bulk of it.
const MEDIA_ICONS = {
  [EMediaType.AUDIO]: Music,
  [EMediaType.VIDEO]: Video,
  [EMediaType.BUNDLE]: FileArchive,
} as const;

const MEDIA_TYPE_LABEL_KEYS = {
  [EMediaType.AUDIO]: "details.downloads.audio",
  [EMediaType.VIDEO]: "details.downloads.video",
  [EMediaType.BUNDLE]: "details.downloads.bundle",
} as const;

// NOTE: One row of the global DownloadIndicator. Memoized so that a progress
// update to one download only re-renders that row, not every active download.
const DownloadIndicatorItemComponent = ({
  meetingUuid,
  downloadingType,
  downloadProgress,
  label,
  onOpen,
  onCancel,
}: IDownloadIndicatorItemProps): JSX.Element => {
  const { t } = useTranslation("callAssistant");

  const MediaIcon = MEDIA_ICONS[downloadingType];
  const typeLabel = t(MEDIA_TYPE_LABEL_KEYS[downloadingType]);

  return (
    <div
      className="flex items-center gap-3 rounded-md p-2"
      data-testid="download-indicator-item"
    >
      <MediaIcon className="h-5 w-5 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          {/* NOTE: Only the title is the link (opens the meeting); text-start
              keeps it correctly aligned in both LTR and RTL. */}
          <button
            type="button"
            onClick={() => onOpen(meetingUuid)}
            className="min-w-0 flex-1 truncate text-start text-sm font-medium hover:underline"
            title={label}
            aria-label={t("details.downloads.open_meeting", { title: label })}
            data-testid="download-indicator-open"
          >
            {label}
          </button>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {downloadProgress}%
          </span>
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">{typeLabel}</div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ width: `${downloadProgress}%` }}
            data-testid="download-indicator-progress"
          />
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={() => onCancel(meetingUuid)}
        aria-label={t("details.downloads.cancel")}
        data-testid="download-indicator-cancel"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

export const DownloadIndicatorItem = memo(DownloadIndicatorItemComponent);
